import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, LogOut, Search, RefreshCw, Users, Inbox, Phone, FileText, ShieldCheck, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { adminApi } from '@/lib/api';
import { WS_URL as LIVE_WS } from '@/lib/live';
import { toast } from 'sonner';
import { Panel, Kpi, StatusPill } from '@/components/enterprise/ui';
import { Switch } from '@/components/ui/switch';
import IntegrationsPanel from '@/components/admin/IntegrationsPanel';
import StatusPanel from '@/components/admin/StatusPanel';

function SettingsPanel() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    adminApi.get('/admin/settings').then((r) => setSettings(r.data)).catch(() => toast.error('Einstellungen konnten nicht geladen werden'));
  }, []);
  const patch = async (key, val) => {
    setSettings((s) => ({ ...s, [key]: val }));
    setSaving(true);
    try {
      const r = await adminApi.patch('/admin/settings', { [key]: val });
      setSettings(r.data);
      toast.success('Gespeichert');
    } catch (e) { toast.error('Fehler beim Speichern'); } finally { setSaving(false); }
  };
  const Row = ({ k, title, desc, testid }) => (
    <div className="flex items-start justify-between gap-4 rounded-xl border border-[color:var(--itw-border)] bg-[color:var(--itw-surface-2)]/50 p-4">
      <div>
        <div className="text-sm font-medium text-[color:var(--itw-text)]">{title}</div>
        <div className="mt-1 text-xs text-[color:var(--itw-text-3)] max-w-md">{desc}</div>
      </div>
      <Switch checked={!!settings?.[k]} onCheckedChange={(v) => patch(k, v)} disabled={saving || !settings} data-testid={testid} />
    </div>
  );
  return (
    <Panel title="Website-Einstellungen" subtitle="Startanimation & Erlebnis" testid="admin-settings-panel">
      {!settings ? (
        <div className="flex items-center gap-2 text-sm text-[color:var(--itw-text-3)]"><Loader2 className="h-4 w-4 animate-spin" /> Lädt…</div>
      ) : (
        <div className="space-y-3 max-w-2xl">
          <Row k="boot_enabled" title="Startup-Boot-Animation" desc="Zeigt beim ersten Laden pro Sitzung die cinematische Boot-Sequenz. Deaktivieren, um Besucher direkt zur Website zu führen." testid="admin-setting-boot-enabled" />
          <Row k="boot_sound_default" title="Boot-Sound standardmäßig an" desc="Legt fest, ob der subtile System-Sound bei der Boot-Animation standardmäßig aktiv ist. Besucher können ihn jederzeit umschalten." testid="admin-setting-boot-sound" />
        </div>
      )}
    </Panel>
  );
}

function AttachmentThumb({ file }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  const isPdf = (file.content_type || '').includes('pdf');
  useEffect(() => {
    let revoked = null;
    let active = true;
    (async () => {
      try {
        const res = await adminApi.get(`/admin/files/${file.id}`, { responseType: 'blob' });
        const objUrl = URL.createObjectURL(res.data);
        revoked = objUrl;
        if (active) setUrl(objUrl);
      } catch (e) { if (active) setErr(true); }
    })();
    return () => { active = false; if (revoked) URL.revokeObjectURL(revoked); };
  }, [file.id]);

  return (
    <a href={url || undefined} target="_blank" rel="noreferrer" data-testid={`admin-attachment-${file.id}`}
      className="group relative block rounded-lg overflow-hidden border border-[color:var(--itw-border)] bg-[color:var(--itw-surface-2)] aspect-square">
      {isPdf || err ? (
        <div className="absolute inset-0 grid place-items-center">
          <FileText className="h-6 w-6 text-[color:var(--itw-text-3)]" />
        </div>
      ) : url ? (
        <img src={url} alt={file.original_filename} className="absolute inset-0 h-full w-full object-cover" />
      ) : (
        <div className="absolute inset-0 grid place-items-center"><Loader2 className="h-4 w-4 animate-spin text-[color:var(--itw-text-3)]" /></div>
      )}
      <div className="absolute bottom-0 inset-x-0 bg-black/60 px-1.5 py-1 text-[9px] font-mono text-[color:var(--itw-text-2)] truncate opacity-0 group-hover:opacity-100 transition-opacity">{file.original_filename}</div>
    </a>
  );
}

const TYPE_TABS = [
  { key: 'all', label: 'Alle' },
  { key: 'offer', label: 'Angebote' },
  { key: 'repair', label: 'Reparaturen' },
  { key: 'contact', label: 'Kontakt' },
  { key: 'callback', label: 'Termine / Rückruf' },
];
const STATUSES = [
  { key: 'new', label: 'Neu', color: '#00E7FF' },
  { key: 'in_progress', label: 'In Bearbeitung', color: '#FFCC66' },
  { key: 'done', label: 'Erledigt', color: '#2EF2A5' },
];

function LoginScreen({ onSuccess }) {
  const [form, setForm] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await adminApi.post('/admin/login', form);
      localStorage.setItem('itw_admin_token', res.data.token);
      window.dispatchEvent(new Event('itw:auth-changed'));
      onSuccess();
    } catch (err) {
      toast.error('Ungültige Anmeldedaten');
    } finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen gradient-hero grid place-items-center px-4">
      <div className="absolute inset-0 itw-grid opacity-40" />
      <motion.form onSubmit={submit} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-sm glass rounded-2xl p-8 neon-edge" data-testid="admin-login">
        <Link to="/" className="inline-flex items-center gap-2 text-xs text-[color:var(--itw-text-3)] hover:text-[color:var(--itw-cyan)] mb-5"><ArrowLeft className="h-3.5 w-3.5" /> Zur Website</Link>
        <div className="flex items-center gap-2 text-[color:var(--itw-cyan)]"><ShieldCheck className="h-5 w-5" /><span className="font-display font-semibold">Admin Console</span></div>
        <h1 className="mt-4 font-display text-xl font-semibold text-[color:var(--itw-text)]">Anmeldung</h1>
        <p className="mt-1 text-sm text-[color:var(--itw-text-2)]">Sicherer Zugang zur Operations-Verwaltung.</p>
        <div className="mt-6 space-y-4">
          <div className="space-y-1.5"><Label>Benutzername</Label><Input value={form.username} onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))} required data-testid="admin-username-input" className="bg-[color:var(--itw-surface-2)] border-[color:var(--itw-border)]" /></div>
          <div className="space-y-1.5"><Label>Passwort</Label><Input type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} required data-testid="admin-password-input" className="bg-[color:var(--itw-surface-2)] border-[color:var(--itw-border)]" /></div>
          <Button type="submit" disabled={loading} data-testid="admin-login-submit" className="w-full h-11 bg-[color:var(--itw-cyan)] text-[color:var(--itw-bg)] font-medium neon-edge hover:opacity-90 transition-opacity">
            {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Anmeldung…</> : 'Anmelden'}
          </Button>
        </div>
      </motion.form>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(() => !!localStorage.getItem('itw_admin_token'));
  const [overview, setOverview] = useState(null);
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [tab, setTab] = useState('all');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState('overview');
  const wsRef = useRef(null);

  const logout = useCallback(() => { localStorage.removeItem('itw_admin_token'); window.dispatchEvent(new Event('itw:auth-changed')); setAuthed(false); }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, ld, us] = await Promise.all([
        adminApi.get('/admin/overview'),
        adminApi.get('/admin/leads', { params: { type: tab, q: q || undefined } }),
        adminApi.get('/admin/users'),
      ]);
      setOverview(ov.data); setLeads(ld.data.leads); setUsers(us.data.users);
    } catch (err) {
      if (err?.response?.status === 401) { toast.error('Sitzung abgelaufen'); logout(); }
    } finally { setLoading(false); }
  }, [tab, q, logout]);

  useEffect(() => { if (authed) fetchAll(); }, [authed, fetchAll]);

  // live admin activity via dedicated authenticated WS
  useEffect(() => {
    if (!authed) return undefined;
    const token = localStorage.getItem('itw_admin_token');
    let ws;
    try {
      ws = new WebSocket(`${LIVE_WS}?token=${encodeURIComponent(token)}&topics=admin/activity`);
      wsRef.current = ws;
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === 'new_lead') { toast.success(`Neue Anfrage: ${msg.name || ''}`); fetchAll(); }
        } catch (err) { /* noop */ }
      };
    } catch (err) { /* noop */ }
    return () => { try { ws && ws.close(); } catch (err) { /* noop */ } };
  }, [authed, fetchAll]);

  const setStatus = async (lead, status) => {
    try {
      await adminApi.patch(`/admin/leads/${lead.id}`, { status });
      setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, status } : l)));
      if (selected && selected.id === lead.id) setSelected({ ...selected, status });
      toast.success('Status aktualisiert');
    } catch (err) { toast.error('Fehler beim Aktualisieren'); }
  };

  const setUserRole = async (u, role) => {
    try {
      await adminApi.patch(`/admin/users/${u.id}/role`, { role });
      setUsers((us) => us.map((x) => (x.id === u.id ? { ...x, role } : x)));
      toast.success('Rolle aktualisiert');
    } catch (err) { toast.error('Fehler'); }
  };

  if (!authed) return <LoginScreen onSuccess={() => setAuthed(true)} />;

  const k = overview?.kpis;
  const fmtDate = (s) => { try { return new Date(s).toLocaleString('de-DE'); } catch (e) { return s; } };

  return (
    <div className="min-h-screen bg-[color:var(--itw-bg)] text-[color:var(--itw-text)]">
      <header className="sticky top-0 z-20 flex items-center justify-between px-4 sm:px-6 py-3 border-b border-[color:var(--itw-border)] bg-[color:var(--itw-bg)]/85 backdrop-blur-md">
        <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-[color:var(--itw-cyan)]" /><span className="font-display font-semibold">IT-WELT360 <span className="text-[color:var(--itw-text-3)] font-normal">Admin Console</span></span></div>
        <div className="flex items-center gap-2">
          <Link to="/plattform" className="inline-flex items-center gap-1.5 rounded-lg border border-[color:var(--itw-border)] px-3 py-1.5 text-xs text-[color:var(--itw-cyan)] hover:border-[color:var(--itw-border-strong)] transition-colors" data-testid="admin-open-platform">Plattform-Dashboards</Link>
          <button onClick={fetchAll} className="inline-flex items-center gap-1.5 rounded-lg border border-[color:var(--itw-border)] px-3 py-1.5 text-xs text-[color:var(--itw-text-2)] hover:border-[color:var(--itw-border-strong)] transition-colors" data-testid="admin-refresh"><RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Aktualisieren</button>
          <button onClick={logout} className="inline-flex items-center gap-1.5 rounded-lg border border-[color:var(--itw-border)] px-3 py-1.5 text-xs text-[color:var(--itw-text-2)] hover:border-[#FF5C7A]/40 hover:text-[#FF5C7A] transition-colors" data-testid="admin-logout"><LogOut className="h-3.5 w-3.5" /> Abmelden</button>
        </div>
      </header>

      <div className="border-b border-[color:var(--itw-border)] px-4 sm:px-6">
        <div className="max-w-[1400px] mx-auto flex gap-1">
          {[['overview', 'Übersicht'], ['integrations', 'Integrationen'], ['status', 'Service-Status'], ['settings', 'Einstellungen']].map(([key, label]) => (
            <button key={key} onClick={() => setView(key)} data-testid={`admin-view-${key}`}
              className={`px-4 py-3 text-sm border-b-2 transition-colors ${view === key ? 'border-[color:var(--itw-cyan)] text-[color:var(--itw-cyan)]' : 'border-transparent text-[color:var(--itw-text-2)] hover:text-[color:var(--itw-text)]'}`}>{label}</button>
          ))}
        </div>
      </div>

      <main className="p-4 sm:p-6 space-y-6 max-w-[1400px] mx-auto">
        {view === 'integrations' && <IntegrationsPanel />}
        {view === 'status' && <StatusPanel />}
        {view === 'settings' && <SettingsPanel />}
        {view === 'overview' && (<>
        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-7 gap-3">
          <Kpi label="Anfragen gesamt" value={k?.total ?? '—'} testid="admin-kpi-total" />
          <Kpi label="Neu" value={k?.new ?? '—'} accent="#00E7FF" testid="admin-kpi-new" />
          <Kpi label="Angebote" value={k?.offers ?? '—'} />
          <Kpi label="Reparaturen" value={k?.repairs ?? '—'} accent="#5AF3FF" testid="admin-kpi-repairs" />
          <Kpi label="Kontakt" value={k?.contacts ?? '—'} />
          <Kpi label="Termine" value={k?.callbacks ?? '—'} accent="#FFCC66" />
          <Kpi label="Benutzer" value={k?.users ?? '—'} accent="#2EF2A5" />
        </div>

        <div className="grid lg:grid-cols-3 gap-4">
          {/* LEADS */}
          <div className="lg:col-span-2 space-y-3">
            <div className="flex flex-wrap items-center gap-2 justify-between">
              <div className="flex flex-wrap gap-1.5">
                {TYPE_TABS.map((t) => (
                  <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`}
                    className={`rounded-full px-3.5 py-1.5 text-xs font-medium border transition-[background-color,border-color,color] duration-200 ${tab === t.key ? 'bg-[color:var(--itw-cyan)] text-[color:var(--itw-bg)] border-[color:var(--itw-cyan)]' : 'text-[color:var(--itw-text-2)] border-[color:var(--itw-border)] hover:border-[color:var(--itw-border-strong)]'}`}>{t.label}</button>
                ))}
              </div>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[color:var(--itw-text-3)]" />
                <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && fetchAll()} placeholder="Suchen…"
                  data-testid="admin-search" className="h-9 w-48 rounded-lg bg-[color:var(--itw-surface-2)] border border-[color:var(--itw-border)] pl-8 pr-3 text-sm text-[color:var(--itw-text)] outline-none focus:border-[color:var(--itw-border-strong)]" />
              </div>
            </div>

            <Panel testid="admin-leads-panel" className="!p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-[11px] uppercase tracking-wider text-[color:var(--itw-text-3)] font-mono text-left border-b border-[color:var(--itw-border)]">
                    <th className="px-4 py-2.5 font-normal">Datum</th><th className="px-2 py-2.5 font-normal">Typ</th><th className="px-2 py-2.5 font-normal">Name</th><th className="px-2 py-2.5 font-normal">Leistung</th><th className="px-2 py-2.5 font-normal">Status</th><th className="px-2 py-2.5 font-normal"></th>
                  </tr></thead>
                  <tbody>
                    {leads.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-[color:var(--itw-text-3)]">Keine Einträge</td></tr>}
                    {leads.map((l) => (
                      <tr key={l.id} className="border-b border-[color:var(--itw-border)] hover:bg-[color:var(--itw-surface-2)]/40 cursor-pointer" onClick={() => setSelected(l)} data-testid={`admin-lead-row`}>
                        <td className="px-4 py-2.5 font-mono text-[12px] text-[color:var(--itw-text-3)] whitespace-nowrap">{fmtDate(l.created_at)}</td>
                        <td className="px-2 py-2.5"><span className="rounded px-1.5 py-0.5 text-[10px] font-mono uppercase bg-[color:var(--itw-surface-2)] text-[color:var(--itw-text-2)]">{l.type}</span></td>
                        <td className="px-2 py-2.5 text-[color:var(--itw-text)]">{l.name}<div className="text-[11px] text-[color:var(--itw-text-3)]">{l.email}</div></td>
                        <td className="px-2 py-2.5 text-[color:var(--itw-text-2)] text-[13px]">{l.service || '—'}</td>
                        <td className="px-2 py-2.5"><StatusPill status={l.status === 'new' ? 'info' : l.status === 'in_progress' ? 'warning' : 'ok'} label={STATUSES.find((s) => s.key === l.status)?.label || l.status} /></td>
                        <td className="px-2 py-2.5 text-right text-[color:var(--itw-text-3)]">›</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          </div>

          {/* SIDE: activity + users */}
          <div className="space-y-4">
            <Panel title="Aktivität" subtitle="audit log" testid="admin-activity">
              <div className="space-y-2 max-h-[220px] overflow-y-auto">
                {(overview?.activity || []).length === 0 && <div className="text-sm text-[color:var(--itw-text-3)]">Keine Aktivität</div>}
                {(overview?.activity || []).map((a) => (
                  <div key={a.id} className="text-[12px] font-mono flex items-center justify-between gap-2">
                    <span className="text-[color:var(--itw-text-2)] truncate">{a.action}</span>
                    <span className="text-[color:var(--itw-text-3)] shrink-0">{fmtDate(a.ts)}</span>
                  </div>
                ))}
              </div>
            </Panel>
            <Panel title="Benutzer & Rollen" subtitle="RBAC" testid="admin-users">
              <div className="space-y-2">
                {users.map((u) => (
                  <div key={u.id} className="rounded-lg border border-[color:var(--itw-border)] p-2.5">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-[color:var(--itw-text)] truncate">{u.name || u.email}</span>
                      <span className="text-[11px] font-mono text-[color:var(--itw-cyan)]">{u.role || 'kunde'}</span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {['kunde', 'support', 'techniker', 'administrator'].map((r) => (
                        <button key={r} onClick={() => setUserRole(u, r)} className={`rounded px-2 py-0.5 text-[10px] font-mono border transition-colors ${(u.role || 'kunde') === r ? 'border-[color:var(--itw-cyan)] text-[color:var(--itw-cyan)]' : 'border-[color:var(--itw-border)] text-[color:var(--itw-text-3)] hover:text-[color:var(--itw-text-2)]'}`}>{r}</button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Panel>
          </div>
        </div>
        </>)}
      </main>

      {/* DETAIL DRAWER */}
      {selected && (
        <div className="fixed inset-0 z-40 flex justify-end" data-testid="admin-lead-detail">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSelected(null)} />
          <motion.div initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} className="relative w-full max-w-md h-full bg-[color:var(--itw-surface)] border-l border-[color:var(--itw-border)] p-6 overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-semibold">Anfrage-Details</h3>
              <button onClick={() => setSelected(null)} className="text-[color:var(--itw-text-3)] hover:text-[color:var(--itw-text)]" data-testid="admin-detail-close">✕</button>
            </div>
            <div className="mt-5 space-y-3 text-sm">
              {[['Name', selected.name], ['E-Mail', selected.email], ['Telefon', selected.phone], ['Firma', selected.company], ['Typ', selected.type], ['Leistung', selected.service], ['Budget', selected.budget], ['Zeitrahmen', selected.timeline], ['Quelle', selected.source_page], ['Sprache', selected.language]].map(([label, val]) => (
                val ? <div key={label} className="flex justify-between gap-4 border-b border-[color:var(--itw-border)] pb-2"><span className="text-[color:var(--itw-text-3)]">{label}</span><span className="text-[color:var(--itw-text)] text-right break-words">{val}</span></div> : null
              ))}
              {selected.message && <div><div className="text-[color:var(--itw-text-3)] mb-1">Nachricht</div><div className="rounded-lg bg-[color:var(--itw-surface-2)] border border-[color:var(--itw-border)] p-3 text-[color:var(--itw-text-2)] whitespace-pre-wrap">{selected.message}</div></div>}
            </div>

            {/* Repair-specific details */}
            {(selected.type === 'repair' || selected.device_type) && (
              <div className="mt-6" data-testid="admin-repair-details">
                <div className="text-[11px] uppercase tracking-wider text-[color:var(--itw-text-3)] font-mono mb-2">Reparatur-Details</div>
                <div className="space-y-3 text-sm">
                  {[['Gerätetyp', selected.device_type], ['Hersteller', selected.manufacturer], ['Modell', selected.model], ['Seriennummer', selected.serial_number], ['Startet', selected.powers_on], ['Dringlichkeit', selected.urgency], ['Wunschtermin', selected.preferred_date]].map(([label, val]) => (
                    val ? <div key={label} className="flex justify-between gap-4 border-b border-[color:var(--itw-border)] pb-2"><span className="text-[color:var(--itw-text-3)]">{label}</span><span className="text-[color:var(--itw-text)] text-right break-words">{val}</span></div> : null
                  ))}
                  {selected.error_description && <div><div className="text-[color:var(--itw-text-3)] mb-1">Fehlerbeschreibung</div><div className="rounded-lg bg-[color:var(--itw-surface-2)] border border-[color:var(--itw-border)] p-3 text-[color:var(--itw-text-2)] whitespace-pre-wrap">{selected.error_description}</div></div>}
                </div>
              </div>
            )}

            {/* Attachments */}
            {Array.isArray(selected.attachments) && selected.attachments.length > 0 && (
              <div className="mt-6" data-testid="admin-attachments">
                <div className="text-[11px] uppercase tracking-wider text-[color:var(--itw-text-3)] font-mono mb-2">Anhänge ({selected.attachments.length})</div>
                <div className="grid grid-cols-3 gap-2">
                  {selected.attachments.map((f) => <AttachmentThumb key={f.id} file={f} />)}
                </div>
              </div>
            )}
            <div className="mt-6">
              <div className="text-[11px] uppercase tracking-wider text-[color:var(--itw-text-3)] font-mono mb-2">Status setzen</div>
              <div className="flex gap-2">
                {STATUSES.map((s) => (
                  <button key={s.key} onClick={() => setStatus(selected, s.key)} data-testid={`admin-set-status-${s.key}`}
                    className={`flex-1 rounded-lg border px-3 py-2 text-xs transition-[border-color,color] ${selected.status === s.key ? '' : 'border-[color:var(--itw-border)] text-[color:var(--itw-text-2)]'}`}
                    style={selected.status === s.key ? { borderColor: s.color, color: s.color } : {}}>{s.label}</button>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
